// Centralized AppState (Phase 5 Step 13 — final extraction; consolidate-state core).
//
// The controlled write-path for the cross-cutting, UI-reactive globals. Every write to
// currentTicker/currentTickerName/currentChartRange and the screener/hub state goes through a
// setter here, so a single, auditable path mutates them. Those first three now live on the shared
// `state` object (src/state.js) — like the rest — because a setter cannot reassign an imported ESM
// binding across files, but it CAN mutate a shared object's property (visible to every reader).
//
// This module is portable CORE: ZERO browser globals (keep the tokens out of comments too, so the
// portability grep stays clean). It imports only state/engineState/Logger and reaches no DOM/global.
// The app.js shell registers it globally and injects the debug-only snapshot extras via
// configure({ getExtras }).
//
// Pub/sub history: earlier revisions carried a DOM CustomEvent bus (_bus/subscribe/notify). It had
// ZERO subscribers anywhere in the codebase, so it was dropped in Step 13 (closing PORT-2 by
// deletion, not injection). The setters keep the one observable effect the old notify() had: a
// leveled Logger.debug on every state change.
import { state } from './state.js';
import { engineState } from './engineState.js';
import { Logger } from './logger.js';

// Debug-only snapshot extras (screenerIndex/groupRanking*/spyBarsCached/…) stay as app.js shell
// lets — hot-path values read only by the console dump — and are injected as one getter to avoid
// migrating six more lets for a debug view. See configure() below.
let _getExtras = () => ({});
export function configure(d) { if (d && typeof d.getExtras === 'function') _getExtras = d.getExtras; }

// Replaces the old notify(): the sole observable effect of a state change (a leveled debug log).
function _emit(event, payload) {
    if (typeof Logger !== 'undefined' && Logger) Logger.debug('[AppState] ' + event, { payload });
}

export const AppState = {
    // ---- controlled setters (write-through to the shared state + debug log) ----
    setCurrentTicker(t) { state.currentTicker = t; _emit('tickerChanged', t); },
    setCurrentTickerName(n) { state.currentTickerName = n; _emit('tickerNameChanged', n); },
    setChartRange(r) { state.currentChartRange = r; _emit('chartRangeChanged', r); },
    setScreenerResults(rows) { state.screenerResults = rows; _emit('screenerResultsChanged', rows); },
    setScreenerRunning(on) { state.screenerRunning = !!on; _emit('screenerRunningChanged', !!on); },
    setActiveHubTab(tab) { state.activeHubTab = tab; _emit('hubTabChanged', tab); },

    // ---- read-only inspection (shape identical to the pre-extraction snapshot) ----
    snapshot() {
        const x = _getExtras();
        return {
            currentTicker: state.currentTicker, currentTickerName: state.currentTickerName, currentChartRange: state.currentChartRange,
            cachedBars: engineState.cachedDataResults?.length ?? 0,
            screenerRunning: state.screenerRunning, screenerPaused: state.screenerPaused, screenerIndex: x.screenerIndex, screenerPreset: state.screenerPreset,
            screenerResults: state.screenerResults?.length ?? 0,
            screenerAllResults: state.screenerAllResults?.length ?? 0,
            activeHubTab: state.activeHubTab, groupRankingDimension: x.groupRankingDimension, groupRankingSort: x.groupRankingSort,
            spyBarsCached: x.spyBarsCached,
            marketStructureIndicesLoaded: x.marketStructureIndicesLoaded,
            lastUnifiedScore: x.lastUnifiedScore, lastMarketStructure: engineState.lastMarketStructure
        };
    },
    dump() { console.table(AppState.snapshot()); return AppState.snapshot(); }
};
